import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

import { COLORS, PROP_DEFINITIONS } from "./dioramaLayout";
import { InteractiveProp } from "./InteractiveProp";

const DEF = PROP_DEFINITIONS.find((p) => p.id === "notebook")!;

/** Visual-only notebook — reusable outside the engine's InteractiveProp wrapper. */
export function NotebookModel() {
  const pencilRef = useRef<THREE.Group>(null);

  // Idle pencil bob — the same "I'm alive, click me" cue as the mailbox flag.
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (pencilRef.current) {
      pencilRef.current.position.y = 0.62 + Math.sin(t * 1.4) * 0.05;
      pencilRef.current.rotation.z = -0.5 + Math.sin(t * 1.1) * 0.08;
    }
  });

  return (
    <group>
      {/* Sand plinth — lifts the notebook so it reads at isometric distance */}
      <mesh position={[0, 0.14, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.85, 1.0, 0.28, 8]} />
        <meshStandardMaterial color={COLORS.sandDark} flatShading />
      </mesh>

      {/* Notebook, slightly rotated so the spread faces the camera */}
      <group position={[0, 0.28, 0]} rotation={[0, -0.5, 0]}>
        {/* Teal cover under the pages */}
        <mesh position={[0, 0.045, 0]} castShadow>
          <boxGeometry args={[1.3, 0.09, 0.95]} />
          <meshStandardMaterial color={COLORS.tealDark} flatShading />
        </mesh>

        {/* Open page spread — two halves in a shallow V */}
        <mesh position={[-0.31, 0.115, 0]} rotation={[0, 0, 0.07]} castShadow>
          <boxGeometry args={[0.6, 0.05, 0.88]} />
          <meshStandardMaterial color={COLORS.cream} flatShading />
        </mesh>
        <mesh position={[0.31, 0.115, 0]} rotation={[0, 0, -0.07]} castShadow>
          <boxGeometry args={[0.6, 0.05, 0.88]} />
          <meshStandardMaterial color={COLORS.white} flatShading />
        </mesh>

        {/* Ink lines on the right page — reads as writing from iso distance */}
        {[-0.22, -0.08, 0.06, 0.2].map((z, i) => (
          <mesh key={z} position={[0.3, 0.145, z]} rotation={[0, 0, -0.07]}>
            <boxGeometry args={[i === 3 ? 0.22 : 0.38, 0.012, 0.035]} />
            <meshStandardMaterial color={COLORS.tealDark} flatShading />
          </mesh>
        ))}

        {/* Spiral binding along the spine */}
        {[-0.36, -0.18, 0, 0.18, 0.36].map((z) => (
          <mesh key={z} position={[0, 0.1, z]} rotation={[0, 0, Math.PI / 2]}>
            <torusGeometry args={[0.055, 0.016, 6, 10]} />
            <meshStandardMaterial color={COLORS.purpleDeep} flatShading />
          </mesh>
        ))}

        {/* Bookmark ribbon spilling off the left page */}
        <mesh position={[-0.52, 0.09, 0.32]} rotation={[0.25, 0.2, 0]} castShadow>
          <boxGeometry args={[0.1, 0.02, 0.42]} />
          <meshStandardMaterial color={COLORS.coral1} flatShading />
        </mesh>
      </group>

      {/* Floating pencil — orange body, sand tip, pink eraser */}
      <group ref={pencilRef} position={[0.55, 0.62, -0.35]} rotation={[0, 0.4, -0.5]}>
        <mesh castShadow>
          <cylinderGeometry args={[0.045, 0.045, 0.55, 6]} />
          <meshStandardMaterial color={COLORS.orange} flatShading />
        </mesh>
        <mesh position={[0, -0.33, 0]}>
          <coneGeometry args={[0.045, 0.11, 6]} />
          <meshStandardMaterial color={COLORS.sand} flatShading />
        </mesh>
        <mesh position={[0, 0.3, 0]}>
          <cylinderGeometry args={[0.048, 0.048, 0.06, 6]} />
          <meshStandardMaterial color={COLORS.pink} flatShading />
        </mesh>
      </group>
    </group>
  );
}

export function OfficeNotebook() {
  return (
    <InteractiveProp
      propId={DEF.id}
      label={DEF.label}
      clickMessage={DEF.clickMessage}
      position={DEF.position}
    >
      {/* 20% smaller than the base model — scales about the ground origin so
          the plinth stays seated on the sand. */}
      <group scale={0.8}>
        <NotebookModel />
      </group>
    </InteractiveProp>
  );
}
